export type DiffLine = {
  type: 'add' | 'delete' | 'same';
  content: string;
  beforeLineNum?: number;
  afterLineNum?: number;
};

const getFrontierValue = (frontier: Map<number, number>, diagonal: number): number =>
  frontier.get(diagonal) ?? 0;

/**
 * Myersの最短編集スクリプトを後ろからたどり、行差分へ変換する。
 *
 * 行の一致を最長共通部分列として扱うため、挿入・削除で後続行がずれても
 * 共通行を同じ行として整列できる。
 */
const backtrack = (
  trace: Map<number, number>[],
  beforeLines: string[],
  afterLines: string[]
): DiffLine[] => {
  const result: DiffLine[] = [];
  let beforeIndex = beforeLines.length;
  let afterIndex = afterLines.length;

  for (let editDistance = trace.length - 1; editDistance > 0; editDistance--) {
    const diagonal = beforeIndex - afterIndex;
    const previousFrontier = trace[editDistance - 1];
    const previousDiagonal =
      diagonal === -editDistance ||
      (diagonal !== editDistance &&
        getFrontierValue(previousFrontier, diagonal - 1) <
          getFrontierValue(previousFrontier, diagonal + 1))
        ? diagonal + 1
        : diagonal - 1;
    const previousBeforeIndex = getFrontierValue(previousFrontier, previousDiagonal);
    const previousAfterIndex = previousBeforeIndex - previousDiagonal;

    while (beforeIndex > previousBeforeIndex && afterIndex > previousAfterIndex) {
      beforeIndex--;
      afterIndex--;
      result.push({
        type: 'same',
        content: beforeLines[beforeIndex],
        beforeLineNum: beforeIndex + 1,
        afterLineNum: afterIndex + 1
      });
    }

    if (beforeIndex === previousBeforeIndex) {
      afterIndex--;
      result.push({type: 'add', content: afterLines[afterIndex], afterLineNum: afterIndex + 1});
    } else {
      beforeIndex--;
      result.push({
        type: 'delete',
        content: beforeLines[beforeIndex],
        beforeLineNum: beforeIndex + 1
      });
    }
  }

  while (beforeIndex > 0 && afterIndex > 0) {
    beforeIndex--;
    afterIndex--;
    result.push({
      type: 'same',
      content: beforeLines[beforeIndex],
      beforeLineNum: beforeIndex + 1,
      afterLineNum: afterIndex + 1
    });
  }

  while (beforeIndex > 0) {
    beforeIndex--;
    result.push({
      type: 'delete',
      content: beforeLines[beforeIndex],
      beforeLineNum: beforeIndex + 1
    });
  }

  while (afterIndex > 0) {
    afterIndex--;
    result.push({type: 'add', content: afterLines[afterIndex], afterLineNum: afterIndex + 1});
  }

  return result.reverse();
};

/**
 * before/afterをMyers diffで比較し、表示側が利用する行番号付きの差分を返す。
 */
export const computeLineDiff = (before: string, after: string): DiffLine[] => {
  const beforeLines = before ? before.split('\n') : [];
  const afterLines = after ? after.split('\n') : [];

  if (beforeLines.length === 0 && afterLines.length === 0) return [];

  const maxEditDistance = beforeLines.length + afterLines.length;
  let frontier = new Map<number, number>([[1, 0]]);
  const trace: Map<number, number>[] = [];

  for (let editDistance = 0; editDistance <= maxEditDistance; editDistance++) {
    const nextFrontier = new Map<number, number>();

    for (let diagonal = -editDistance; diagonal <= editDistance; diagonal += 2) {
      let beforeIndex: number;

      if (
        diagonal === -editDistance ||
        (diagonal !== editDistance &&
          getFrontierValue(frontier, diagonal - 1) < getFrontierValue(frontier, diagonal + 1))
      ) {
        // 下方向へ進む: after側の行を追加する。
        beforeIndex = getFrontierValue(frontier, diagonal + 1);
      } else {
        // 右方向へ進む: before側の行を削除する。
        beforeIndex = getFrontierValue(frontier, diagonal - 1) + 1;
      }

      let afterIndex = beforeIndex - diagonal;
      while (
        beforeIndex < beforeLines.length &&
        afterIndex < afterLines.length &&
        beforeLines[beforeIndex] === afterLines[afterIndex]
      ) {
        beforeIndex++;
        afterIndex++;
      }

      nextFrontier.set(diagonal, beforeIndex);

      if (beforeIndex >= beforeLines.length && afterIndex >= afterLines.length) {
        trace.push(nextFrontier);
        return backtrack(trace, beforeLines, afterLines);
      }
    }

    trace.push(nextFrontier);
    frontier = nextFrontier;
  }

  return [];
};
